import React from "react"
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useCuttingPlanStore } from "../../../../stores/cuttingPlanStore";
import { useCuttingPlanStatsStore } from "../../../../stores/cuttingPlanStatsStore";
import {
    getTotalWeightExceptPreCutWaste,
    getTotalWasteWeightExceptPreCut,
    getTotalPreCutWeight,
    getOrderWeight,
    getOutageWeight,
    getSubstitutionWeight,
    getYieldPercentage,
    getWastePercentage,
    getYieldExceptPreCutPercentage,
    getWasteExceptPreCutPercentage,
    getNumberOfTotalLogs,
    getNumberOfOrderLogs,
    getTheoreticalWeight,
    getOrderWeightExceptPreCut
} from "../../../../services/statistics/patternStatistics";

const RebarCpSummary = () => {
    const { t } = useTranslation();

    const cuttingPlanStats = useCuttingPlanStatsStore(state => state.cuttingPlanStats);
    const allDiameters = useCuttingPlanStore(state => state.allDiameters);
    const allFloors = useCuttingPlanStore(state => state.allFloors);
    const allPatterns = useCuttingPlanStore(state => state.allPatterns);

    const rebarInformation = useCuttingPlanStore(state => state.rebarInformation);

    const thinDiameters = allDiameters.filter(d => parseInt(d.value) <= 12).sort((a, b) => a.value - b.value);
    const thickDiameters = allDiameters.filter(d => parseInt(d.value) > 12).sort((a, b) => a.value - b.value);

    const minOutageLength = useCuttingPlanStore(state => state.minOutageLength);
    const currentFloors = useCuttingPlanStore(state => state.checkedFloors);

    const statsFunctions = [
        {
            key: `${t("Teorik Ağırlık")} (kg)`,
            aggregateDiameterCalculation: true,
            aggregateFloorCalculation: false,
            func: (_, floorsUntilTop, diameters) => getTheoreticalWeight(rebarInformation, allFloors, diameters, floorsUntilTop)
        },
        {
            key: `${t("Verim Yüzdesi")} (%)`,
            aggregateDiameterCalculation: true,
            aggregateFloorCalculation: true,
            func: getYieldPercentage
        },
        {
            key: `${t("Fire Yüzdesi")} (%)`,
            aggregateDiameterCalculation: true,
            aggregateFloorCalculation: true,
            func: getWastePercentage
        },
        {
            key: `${t("Verim Yüzdesi (Hazır Kesim Hariç)")} (%)`,
            aggregateDiameterCalculation: true,
            aggregateFloorCalculation: true,
            func: getYieldExceptPreCutPercentage
        },
        {
            key: `${t("Fire Yüzdesi (Hazır Kesim Hariç)")} (%)`,
            aggregateDiameterCalculation: true,
            aggregateFloorCalculation: true,
            func: getWasteExceptPreCutPercentage
        },
        {
            key: `${t("Toplam Ağırlık")} (kg)`,
            aggregateDiameterCalculation: true,
            aggregateFloorCalculation: true,
            func: getTotalWeightExceptPreCutWaste
        },
        {
            key: `${t("Toplam Boy Sayısı")}`,
            aggregateDiameterCalculation: false,
            aggregateFloorCalculation: true,
            func: getNumberOfTotalLogs
        },
        {
            key: `${t("Teorik Fire Ağırlığı")} (kg)`,
            aggregateDiameterCalculation: true,
            aggregateFloorCalculation: true,
            func: getTotalWasteWeightExceptPreCut
        },
        {
            key: `${t("Sipariş Ağırlığı")} (kg)`,
            aggregateDiameterCalculation: true,
            aggregateFloorCalculation: true,
            func: (patterns, floorsUntilTop) => getOrderWeight(patterns, floorsUntilTop)
        },
        {
            key: `${t("Sipariş Ağırlığı (Hazır Kesim Hariç)")} (kg)`,
            aggregateDiameterCalculation: true,
            aggregateFloorCalculation: true,
            func: (patterns, floorsUntilTop) => getOrderWeightExceptPreCut(patterns, floorsUntilTop)
        },
        {
            key: `${t("Sipariş Boy Sayısı")}`,
            aggregateDiameterCalculation: false,
            aggregateFloorCalculation: true,
            func: (patterns, floorsUntilTop) => getNumberOfOrderLogs(patterns, floorsUntilTop)
        },
        {
            key: `${t("Elde Kalan Fire Ağırlığı")} (kg)`,
            aggregateDiameterCalculation: true,
            aggregateFloorCalculation: true,
            func: (patterns) => getOutageWeight(patterns, minOutageLength)
        },
        {
            key: `${t("Hazır Kesimli Donatı Ağırlığı")} (kg)`,
            aggregateDiameterCalculation: true,
            aggregateFloorCalculation: true,
            func: getTotalPreCutWeight
        },
        {
            key: `${t("Yerine Kullanım Ağırlığı")} (kg)`,
            aggregateDiameterCalculation: true,
            aggregateFloorCalculation: true,
            func: getSubstitutionWeight
        },
    ];

    const getFloorsUntilHighest = (floor) => {
        return allFloors.filter(f => f.value >= floor).map(f => f.value);
    }

    return (
        <Container fluid className='border border-top-0 p-3' style={{ backgroundColor: 'white' }}>
            <div style={{ textAlign: "right", marginBottom: "5px" }}>
                <i dangerouslySetInnerHTML={{
                    __html: t("statisticsInfo", {
                        link: t("statisticsInfoLink")
                    })
                }} />
            </div>
            <h5 className="text-center">{t("Mevcut Seçim Sonuç Özeti")}</h5>
            <div className="table-responsive">
                <table className="table table-bordered table-striped-columns">
                    <tbody>
                        <tr className="d-md-table-row d-none">
                            <th style={{ width: "30%" }}>{t("Teorik Ağırlık")} (kg)</th>
                            <td style={{ width: "20%" }}>{cuttingPlanStats.theoreticalWeight}</td>
                            <th style={{ width: "30%" }}>{t("Toplam Ağırlık")} (kg)</th>
                            <td style={{ width: "20%" }}>{cuttingPlanStats.totalWeight}</td>
                        </tr>
                        <tr className="d-md-table-row d-none">
                            <th style={{ width: "30%" }}>{t("Teorik Fire Ağırlığı")} (kg)</th>
                            <td style={{ width: "20%" }}>{cuttingPlanStats.totalWasteWeight}</td>
                            <th style={{ width: "30%" }}>{t("Elde Kalan Fire Ağırlığı")} (kg)</th>
                            <td style={{ width: "20%" }}>{cuttingPlanStats.outageWeight}</td>
                        </tr>
                        <tr className="d-md-table-row d-none">
                            <th style={{ width: "30%" }}>{t("Sipariş Ağırlığı")} (kg)</th>
                            <td style={{ width: "20%" }}>{cuttingPlanStats.orderWeight}</td>
                            <th style={{ width: "30%" }}>{t("Sipariş Ağırlığı (Hazır Kesim Hariç)")} (kg)</th>
                            <td style={{ width: "20%" }}>{cuttingPlanStats.orderWeightExceptPreCut}</td>
                        </tr>
                        <tr className="d-md-table-row d-none">
                            <th style={{ width: "30%" }}>{t("Toplam Boy Sayısı")}</th>
                            <td style={{ width: "20%" }}>{cuttingPlanStats.totalLogs}</td>
                            <th style={{ width: "30%" }}>{t("Sipariş Boy Sayısı")}</th>
                            <td style={{ width: "20%" }}>{cuttingPlanStats.orderLogs}</td>
                        </tr>
                        <tr className="d-md-table-row d-none">
                            <th style={{ width: "30%" }}>{t("Hazır Kesimli Donatı Ağırlığı")} (kg)</th>
                            <td style={{ width: "20%" }}>{cuttingPlanStats.preCutWeight}</td>
                            <th style={{ width: "30%" }}>{t("Yerine Kullanım Ağırlığı")} (kg)</th>
                            <td style={{ width: "20%" }}>{cuttingPlanStats.subWeight}</td>
                        </tr>
                        <tr className="d-md-table-row d-none">
                            <th style={{ width: "30%" }}>{t("Verim Yüzdesi")} (%)</th>
                            <td style={{ width: "20%" }}>{cuttingPlanStats.yieldPercentage}</td>
                            <th style={{ width: "30%" }}>{t("Fire Yüzdesi")} (%)</th>
                            <td style={{ width: "20%" }}>{cuttingPlanStats.wastePercentage}</td>
                        </tr>
                        <tr className="d-md-table-row d-none">
                            <th style={{ width: "30%" }}>{t("Verim Yüzdesi (Hazır Kesim Hariç)")} (%)</th>
                            <td style={{ width: "20%" }}>{cuttingPlanStats.yieldExceptPreCutPercentage}</td>
                            <th style={{ width: "30%" }}>{t("Fire Yüzdesi (Hazır Kesim Hariç)")} (%)</th>
                            <td style={{ width: "20%" }}>{cuttingPlanStats.wasteExceptPreCutPercentage}</td>
                        </tr>
                        {/* Mobile view: */}
                        <tr className="d-md-none d-table-row">
                            <th colSpan="2">{t("Toplam Ağırlık")} (kg)</th>
                            <td colSpan="2">{cuttingPlanStats.totalWeight}</td>
                        </tr>
                        <tr className="d-md-none d-table-row">
                            <th colSpan="2">{t("Teorik Fire Ağırlığı")} (kg)</th>
                            <td colSpan="2">{cuttingPlanStats.totalWasteWeight}</td>
                        </tr>
                        <tr className="d-md-none d-table-row">
                            <th colSpan="2">{t("Sipariş Ağırlığı")} (kg)</th>
                            <td colSpan="2">{cuttingPlanStats.orderWeight}</td>
                        </tr>
                        <tr className="d-md-none d-table-row">
                            <th colSpan="2">{t("Elde Kalan Fire Ağırlığı")} (kg)</th>
                            <td colSpan="2">{cuttingPlanStats.outageWeight}</td>
                        </tr>
                        <tr className="d-md-none d-table-row">
                            <th colSpan="2">{t("Toplam Boy Sayısı")}</th>
                            <td colSpan="2">{cuttingPlanStats.totalLogs}</td>
                        </tr>
                        <tr className="d-md-none d-table-row">
                            <th colSpan="2">{t("Sipariş Boy Sayısı")}</th>
                            <td colSpan="2">{cuttingPlanStats.orderLogs}</td>
                        </tr>
                        <tr className="d-md-none d-table-row">
                            <th colSpan="2">{t("Hazır Kesimli Donatı Ağırlığı")} (kg)</th>
                            <td colSpan="2">{cuttingPlanStats.preCutWeight}</td>
                        </tr>
                        <tr className="d-md-none d-table-row">
                            <th colSpan="2">{t("Yerine Kullanım Ağırlığı")} (kg)</th>
                            <td colSpan="2">{cuttingPlanStats.subWeight}</td>
                        </tr>
                        <tr className="d-md-none d-table-row">
                            <th colSpan="2">{t("Verim Yüzdesi")} (%)</th>
                            <td colSpan="2">{cuttingPlanStats.yieldPercentage}</td>
                        </tr>
                        <tr className="d-md-none d-table-row">
                            <th colSpan="2">{t("Fire Yüzdesi")} (%)</th>
                            <td colSpan="2">{cuttingPlanStats.wastePercentage}</td>
                        </tr>
                        <tr className="d-md-none d-table-row">
                            <th colSpan="2">{t("Verim Yüzdesi (Hazır Kesim Hariç)")} (%)</th>
                            <td colSpan="2">{cuttingPlanStats.yieldExceptPreCutPercentage}</td>
                        </tr>
                        <tr className="d-md-none d-table-row">
                            <th colSpan="2">{t("Fire Yüzdesi (Hazır Kesim Hariç)")} (%)</th>
                            <td colSpan="2">{cuttingPlanStats.wasteExceptPreCutPercentage}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <h5 className="text-center mt-2">{t("Tüm Sonuç Özeti")}</h5>

            {statsFunctions.map((stat, i) => {
                return <div className="mt-2" key={i}>
                    <p className="fw-bold">{stat.key}</p>
                    <div className='table-responsive'>
                        <table className="table border table-hover">
                            <thead>
                                <tr>
                                    <th style={{ width: `${100 / (allDiameters.length + 4)}%` }}></th>
                                    {thinDiameters.map((diameter, i) => (
                                        <th key={i}>{diameter.label}</th>
                                    ))}
                                    {stat.aggregateDiameterCalculation &&
                                        <th style={{ width: `${100 / (allDiameters.length + 4)}%` }}>{t("İnce")}</th>
                                    }
                                    {thickDiameters.map((diameter, i) => (
                                        <th key={i}>{diameter.label}</th>
                                    ))}
                                    {stat.aggregateDiameterCalculation &&
                                        <th style={{ width: `${100 / (allDiameters.length + 4)}%` }}>{t("Kalın")}</th>
                                    }
                                    <th style={{ width: `${100 / (allDiameters.length + 4)}%` }}>{t("TÜMÜ")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allFloors.map((floor, i) => (
                                    <tr key={i}>
                                        <th>{floor.label}</th>
                                        {thinDiameters.map((diameter, j) => {
                                            const patterns = allPatterns.filter(pattern => pattern.floors.includes(floor.value) && pattern.diameter === diameter.value);
                                            return (
                                                <td key={j}>
                                                    {stat.func(
                                                        patterns,
                                                        stat.aggregateFloorCalculation ? getFloorsUntilHighest(floor.value) : [floor.value],
                                                        [diameter.value]
                                                    )}
                                                </td>);
                                        })}
                                        {stat.aggregateDiameterCalculation &&
                                            <td>
                                                {stat.func(
                                                    allPatterns.filter(pattern =>
                                                        pattern.floors.includes(floor.value)
                                                        && thinDiameters.map(d => d.value).includes(pattern.diameter)
                                                    ),
                                                    stat.aggregateFloorCalculation ? getFloorsUntilHighest(floor.value) : [floor.value],
                                                    thinDiameters.map(d => d.value)
                                                )}
                                            </td>
                                        }
                                        {thickDiameters.map((diameter, j) => {
                                            const patterns = allPatterns.filter(pattern => pattern.floors.includes(floor.value) && pattern.diameter === diameter.value);
                                            return (
                                                <td key={j}>
                                                    {stat.func(
                                                        patterns,
                                                        stat.aggregateFloorCalculation ? getFloorsUntilHighest(floor.value) : [floor.value],
                                                        [diameter.value]
                                                    )}
                                                </td>);
                                        })}
                                        {stat.aggregateDiameterCalculation &&
                                            <td>
                                                {stat.func(
                                                    allPatterns.filter(pattern =>
                                                        pattern.floors.includes(floor.value)
                                                        && thickDiameters.map(d => d.value).includes(pattern.diameter)
                                                    ),
                                                    stat.aggregateFloorCalculation ? getFloorsUntilHighest(floor.value) : [floor.value],
                                                    thickDiameters.map(d => d.value)
                                                )}
                                            </td>
                                        }
                                        <th>
                                            {stat.func(
                                                allPatterns.filter(pattern =>
                                                    pattern.floors.includes(floor.value)
                                                ),
                                                stat.aggregateFloorCalculation ? getFloorsUntilHighest(floor.value) : [floor.value],
                                                thinDiameters.map(d => d.value).concat(thickDiameters.map(d => d.value))
                                            )}
                                        </th>
                                    </tr>
                                ))}
                                <tr>
                                    <th>{t("TÜMÜ")}</th>
                                    {thinDiameters.map((diameter, j) => {
                                        const patterns = allPatterns.filter(pattern => pattern.diameter === diameter.value);
                                        return (
                                            <th key={j}>
                                                {stat.func(
                                                    patterns,
                                                    allFloors.map(f => f.value),
                                                    [diameter.value]
                                                )}
                                            </th>);
                                    })}
                                    {stat.aggregateDiameterCalculation &&
                                        <th>
                                            {stat.func(
                                                allPatterns.filter(pattern =>
                                                    thinDiameters.map(d => d.value).includes(pattern.diameter)
                                                ),
                                                allFloors.map(f => f.value),
                                                thinDiameters.map(d => d.value)
                                            )}
                                        </th>
                                    }
                                    {thickDiameters.map((diameter, j) => {
                                        const patterns = allPatterns.filter(pattern => pattern.diameter === diameter.value);
                                        return (
                                            <th key={j}>
                                                {stat.func(
                                                    patterns,
                                                    allFloors.map(f => f.value),
                                                    [diameter.value]
                                                )}
                                            </th>);
                                    })}
                                    {stat.aggregateDiameterCalculation &&
                                        <th>
                                            {stat.func(
                                                allPatterns.filter(pattern =>
                                                    thickDiameters.map(d => d.value).includes(pattern.diameter)
                                                ),
                                                allFloors.map(f => f.value),
                                                thickDiameters.map(d => d.value)
                                            )}
                                        </th>
                                    }
                                    <th>
                                        {stat.func(
                                            allPatterns,
                                            allFloors.map(f => f.value),
                                            thinDiameters.map(d => d.value).concat(thickDiameters.map(d => d.value))
                                        )}
                                    </th>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            })}

        </Container>
    )
}

export default RebarCpSummary;